/**
 * Admin Portal — Asset Management page.
 * View all uploaded assets, approve/reject pending uploads,
 * manage asset access rules, and control client visibility.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Typography, Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Tabs, Tab,
  IconButton, Tooltip,
} from '@mui/material';
import {
  CheckCircle, Cancel, Delete, HourglassEmpty,
  ThumbUp, ThumbDown,
} from '@mui/icons-material';
import { authFetch } from '../../../api/authFetch';
import RefreshButton from '../../../components/RefreshButton';

interface AssetUploadItem {
  id: string;
  assessment_type: string;
  asset_class: string;
  filename: string;
  description: string | null;
  uploaded_by: string;
  uploaded_by_name: string | null;
  status: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  created_at: string | null;
}

const STATUS_COLORS: Record<string, 'warning' | 'success' | 'error'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
};
const STATUS_FILTERS = ['', 'pending', 'approved', 'rejected'];

const AssetManagement: React.FC = () => {
  const [assets, setAssets] = useState<AssetUploadItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0); // 0=All, 1=Pending, 2=Approved, 3=Rejected
  const [actionDialog, setActionDialog] = useState<{ asset: AssetUploadItem; action: 'approve' | 'reject' } | null>(null);
  const [actionNotes, setActionNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const statusParam = STATUS_FILTERS[tab] ? `?status=${STATUS_FILTERS[tab]}` : '';
      const res = await authFetch(`/api/admin/portal/assets${statusParam}`);
      const data = await res.json();
      if (data.success) setAssets(data.assets);
      else setError(data.error || 'Failed to load assets');
    } catch {
      setError('Failed to load assets');
    } finally {
      setLastRefreshed(new Date());
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  const handleAction = async () => {
    if (!actionDialog) return;
    const { asset, action } = actionDialog;

    if (action === 'reject' && !actionNotes.trim()) {
      setError('Rejection reason is required');
      return;
    }

    setActionLoading(true);
    try {
      const res = await authFetch(`/api/admin/portal/assets/${asset.id}/${action}`, {
        method: 'PUT',
        body: JSON.stringify({ notes: actionNotes }),
      });
      const data = await res.json();
      if (data.success) {
        setActionDialog(null);
        setActionNotes('');
        fetchAssets();
      } else {
        setError(data.error || `Failed to ${action} asset`);
      }
    } catch {
      setError(`Failed to ${action} asset`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (assetId: string) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;
    try {
      const res = await authFetch(`/api/admin/portal/assets/${assetId}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) fetchAssets();
      else setError(data.error || 'Failed to delete asset');
    } catch {
      setError('Failed to delete asset');
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
        Asset Management
      </Typography>
        <RefreshButton onRefresh={fetchAssets} loading={loading} lastRefreshed={lastRefreshed} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Review and approve assets uploaded by SMEs before they become available to clients.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="All" />
        <Tab label="Pending Approval" icon={<HourglassEmpty />} iconPosition="start" />
        <Tab label="Approved" icon={<CheckCircle />} iconPosition="start" />
        <Tab label="Rejected" icon={<Cancel />} iconPosition="start" />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : assets.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No assets found.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Filename</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Assessment Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Class</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Uploaded By</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assets.map((asset) => (
                <TableRow key={asset.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{asset.filename}</Typography>
                    {asset.description && (
                      <Typography variant="caption" color="text.secondary">{asset.description}</Typography>
                    )}
                  </TableCell>
                  <TableCell>{asset.assessment_type}</TableCell>
                  <TableCell><Chip label={asset.asset_class} size="small" variant="outlined" /></TableCell>
                  <TableCell>{asset.uploaded_by_name || asset.uploaded_by}</TableCell>
                  <TableCell>
                    {asset.created_at ? new Date(asset.created_at).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={asset.status}
                      size="small"
                      color={STATUS_COLORS[asset.status] || 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {asset.status === 'pending' && (
                      <>
                        <Tooltip title="Approve">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => setActionDialog({ asset, action: 'approve' })}
                          >
                            <ThumbUp fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setActionDialog({ asset, action: 'reject' })}
                          >
                            <ThumbDown fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDelete(asset.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Approve/Reject Dialog */}
      <Dialog open={!!actionDialog} onClose={() => setActionDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionDialog?.action === 'approve' ? 'Approve Asset' : 'Reject Asset'}
        </DialogTitle>
        <DialogContent>
          {actionDialog && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>File:</strong> {actionDialog.asset.filename}
              </Typography>
              <Typography variant="body2">
                <strong>Class:</strong> {actionDialog.asset.asset_class}
              </Typography>
              <Typography variant="body2">
                <strong>Uploaded by:</strong> {actionDialog.asset.uploaded_by_name}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label={actionDialog?.action === 'reject' ? 'Rejection Reason (required)' : 'Notes (optional)'}
            value={actionNotes}
            onChange={(e) => setActionNotes(e.target.value)}
            required={actionDialog?.action === 'reject'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setActionDialog(null); setActionNotes(''); }}>Cancel</Button>
          <Button
            onClick={handleAction}
            variant="contained"
            color={actionDialog?.action === 'approve' ? 'success' : 'error'}
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : actionDialog?.action === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssetManagement;
